import React, { useMemo } from "react"
import PropTypes from "prop-types"
import SelectMenu from "@kit/design/select-menu"
import Icon from "@kit/design/icon"
import Badge from "@kit/design/badge"
import { PaidFeatureBadge } from "app/navigation/utils/paid-feature-badge"
import { canNavigateToApp } from "../utils"
import unsplashLogo from "../unsplash.png"
import instagramLogo from "../instagram-filled.png"
import heroPatternsLogo from "../hero-patterns.png"

const MenuOption = ({
  icon,
  imageUrl,
  label,
  subtitle,
  isPaidFeature,
  needsReauthenticate
}) => (
  <div className="flex items-center gap-2">
    {icon && (
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon name={icon} />
      </div>
    )}
    {imageUrl && (
      <img
        src={imageUrl}
        className="border border-gray-200 rounded-sm w-6 h-6 object-cover"
        alt=""
      />
    )}
    <div className="flex flex-col">
      <span className="flex items-center gap-1">
        {label}
        {needsReauthenticate && (
          <Badge className="ml-1" color="orange" size="sm">
            Update required
          </Badge>
        )}
        {isPaidFeature && !needsReauthenticate && (
          <PaidFeatureBadge className="ml-1" />
        )}
      </span>
      {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
    </div>
  </div>
)

const builtInTabOptions = [
  {
    label: <MenuOption icon="cloud-upload" label="Uploads" />,
    value: "filekit",
    icon: "cloud-upload",
    component: <MenuOption icon="cloud-upload" label="Uploads" />
  },
  {
    label: <MenuOption icon="heart" label="Favorites" />,
    value: "favorites",
    icon: "heart",
    component: <MenuOption icon="heart" label="Favorites" />
  },
  {
    label: <MenuOption icon="refresh" label="Recently used" />,
    value: "recentlyUsed",
    icon: "refresh",
    component: <MenuOption icon="refresh" label="Recently used" />
  }
]

const unsplashTab = {
  label: (
    <MenuOption
      imageUrl={unsplashLogo}
      label="Unsplash"
      subtitle="Beautiful, free images"
    />
  ),
  value: "unsplash",
  imageUrl: unsplashLogo,
  component: (
    <MenuOption
      imageUrl={unsplashLogo}
      label="Unsplash"
      subtitle="Beautiful, free images"
    />
  )
}

const instagramTab = {
  label: (
    <MenuOption
      imageUrl={instagramLogo}
      label="Instagram"
      subtitle="Photos from your profile"
    />
  ),
  value: "instagram",
  imageUrl: instagramLogo,
  component: (
    <MenuOption
      imageUrl={instagramLogo}
      label="Instagram"
      subtitle="Photos from your profile"
    />
  )
}

const heroPatternsTab = {
  label: (
    <MenuOption
      imageUrl={heroPatternsLogo}
      label="Hero Patterns"
      subtitle="Free seamless patterns"
    />
  ),
  value: "heroPatterns",
  imageUrl: heroPatternsLogo,
  component: (
    <MenuOption
      imageUrl={heroPatternsLogo}
      label="Hero Patterns"
      subtitle="Free seamless patterns"
    />
  )
}

const pluginTabOptions = plugins =>
  (plugins || []).map(plugin => {
    const app = plugin.provider?.app
    const navigable = canNavigateToApp(plugin)
    const isDisabled = plugin.needs_reauthenticate && !navigable

    return {
      label: (
        <MenuOption
          imageUrl={plugin.icon_url}
          label={plugin.label}
          isPaidFeature={plugin.can_use === false}
          needsReauthenticate={plugin.needs_reauthenticate}
        />
      ),
      value: plugin.id,
      imageUrl: plugin.icon_url,
      disabled: isDisabled,
      appId: navigable ? app.id : null,
      needsReauthenticate: plugin.needs_reauthenticate,
      component: (
        <MenuOption
          imageUrl={plugin.icon_url}
          label={plugin.label}
          isPaidFeature={plugin.can_use === false}
          needsReauthenticate={plugin.needs_reauthenticate}
        />
      )
    }
  })

const integrationTabOptions = (disabledIntegrations, enablePatterns) => {
  const tabs = []
  if (!disabledIntegrations.includes("unsplash")) tabs.push(unsplashTab)
  if (!disabledIntegrations.includes("instagram")) tabs.push(instagramTab)
  if (enablePatterns && !disabledIntegrations.includes("heroPatterns"))
    tabs.push(heroPatternsTab)
  return tabs
}

export function GalleryPickerSelect({
  activeTab,
  onTabChange,
  plugins,
  disabledIntegrations,
  enablePatterns
}) {
  const allTabOptions = useMemo(
    () => [
      ...builtInTabOptions,
      ...integrationTabOptions(disabledIntegrations, enablePatterns),
      ...pluginTabOptions(plugins)
    ],
    [plugins, disabledIntegrations, enablePatterns]
  )

  const handleTabSelect = event => {
    onTabChange(event.target.value)
  }

  return (
    <SelectMenu
      value={activeTab}
      onChange={handleTabSelect}
      options={allTabOptions}
      className="w-full"
    />
  )
}

GalleryPickerSelect.propTypes = {
  activeTab: PropTypes.string.isRequired,
  plugins: PropTypes.array,
  disabledIntegrations: PropTypes.array.isRequired,
  enablePatterns: PropTypes.bool,
  onTabChange: PropTypes.func.isRequired
}
